/**
 * Activos.tsx — los bienes que se deprecian, y la depreciación del mes.
 *
 * ── ▶ LO PRIMERO QUE SE VE ES QUÉ VA A PASAR, NO EL BOTÓN ─────────────────
 *
 * Depreciar escribe un asiento en los libros. Así que la pantalla enseña la
 * cuota de cada bien ANTES de ofrecer el botón: quien lo aprieta ya sabe el
 * número. Un botón que sólo se entiende apretándolo es la trampa de E8.4, y acá
 * el precio de averiguar es un asiento.
 *
 * ── Y dice que correr dos veces no duplica ────────────────────────────────
 *
 * Porque la duda natural —«¿y si le doy otra vez?»— tiene una respuesta buena
 * que la pantalla se guardaría: la segunda corrida borra el asiento anterior y
 * lo regenera. Sin decirlo, alguien no lo aprieta por miedo y lo hace a mano.
 */
import { useCallback, useEffect, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { anioYMesDeHoy } from '../nucleo/hoy.ts';
import { puedeRegistrar, useMesDeTrabajo, useSesion } from '../nucleo/sesion.tsx';
import { nombreDe, useCuentasDeMovimiento } from '../nucleo/catalogo.tsx';
import { CampoDeDimension } from '../componentes/CampoDeDimension.tsx';
import {
  agregarActivo, depreciarPeriodo, traerActivos, FalloDeApi,
  type ActivoDeLaEmpresa, type CuotaPrevista,
} from '../datos/libros.ts';

const MESES = Array.from({ length: 12 }, (_, i) => i + 1);

export function Activos() {
  const { t, monto, fecha, idioma } = useIdioma();
  const { pedir, sesion } = useSesion();
  // Hoja y activa: el hook ya existía y explica por qué.
  const hojas = useCuentasDeMovimiento();

  /* ▶ Y ADEMÁS DEL TIPO QUE CORRESPONDE. Lo enseñó abrir la pantalla.
   *
   * Los tres desplegables ofrecían las noventa y seis cuentas del catálogo:
   * «Capital social» y «Mantenimiento y soporte» entre las candidatas a gasto de
   * depreciación. La base las rechaza igual —o peor: las acepta y deja un
   * asiento absurdo—, así que ofrecerlas es ofrecer un error.
   *
   * Es la mitad que faltaba del comentario de `useCuentasDeMovimiento`: *ofrecer
   * una cuenta de mayor deja que alguien la elija y se entere al guardar*. Yo
   * había aplicado hoja-y-activa y no el tipo.
   *
   * El bien y su depreciación acumulada son ACTIVO —la acumulada es una cuenta
   * de activo con naturaleza acreedora, no un pasivo—; el gasto es GASTO. */
  const deTipo = (t: string) => hojas.filter((c) => c.tipo === t);
  const cuentasDeActivo = deTipo('asset');
  const cuentasDeGasto = deTipo('expense');
  const escribe = puedeRegistrar(sesion);

  // `hoy` es el calendario, para la fecha de compra y de inicio de un activo
  // (hechos reales). El SELECTOR de mes parte del mes de trabajo (el abierto más
  // viejo sin cerrar), no del calendario.
  const hoy = anioYMesDeHoy();
  const trabajo = useMesDeTrabajo();
  const [anio, setAnio] = useState(trabajo.anio);
  const [mes, setMes] = useState(trabajo.mes);

  const [activos, setActivos] = useState<readonly ActivoDeLaEmpresa[]>([]);
  const [previo, setPrevio] = useState<readonly CuotaPrevista[]>([]);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [hecho, setHecho] = useState<string | null>(null);
  const [abrirForm, setAbrirForm] = useState(false);

  const [nuevo, setNuevo] = useState({
    descripcion: '', cuentaActivo: '', cuentaDepreciacion: '', cuentaGasto: '',
    fechaCompra: `${hoy.anio}-${String(hoy.mes).padStart(2, '0')}-01`,
    inicio: `${hoy.anio}-${String(hoy.mes).padStart(2, '0')}-01`,
    costo: '', vidaMeses: '36', vidaFiscalMeses: '', valorResidual: '0', department: '',
  });

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      const r = await traerActivos(pedir, anio, mes);
      setActivos(r.activos);
      setPrevio(r.previo);
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    }
  }, [pedir, anio, mes]);

  useEffect(() => { void cargar(); }, [cargar]);

  async function correr(fn: () => Promise<unknown>) {
    setOcupado(true);
    setFallo(null);
    try {
      await fn();
      await cargar();
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  async function depreciar() {
    setHecho(null);
    await correr(async () => {
      const r = await depreciarPeriodo(pedir, anio, mes);
      // ▶ «1 bien(es)» lo dijo la pantalla y no lo dijo ninguna prueba. La casa
      // ya tiene la convención —la clave `…One` al lado, como `cat.countOne`—;
      // lo que faltaba era usarla.
      setHecho(r.asiento === null
        ? t('act.doneNone')
        : r.activos === 1
          ? t('act.doneOkOne', { asiento: String(r.asiento) })
          : t('act.doneOk', { activos: String(r.activos), asiento: String(r.asiento) }));
    });
  }

  const total = previo.reduce((n, p) => n + Math.round(Number(p.cuota) * 100), 0) / 100;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t('act.title')}</h1>
          <p className="page-head__sub">{t('act.sub')}</p>
        </div>
      </div>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}
      {hecho && (
        <div className="alert alert--ok" style={{ marginBottom: 'var(--s6)' }}>
          <span>{hecho}</span>
        </div>
      )}

      {/* ── El mes, y lo que le tocaría ─────────────────────────────────── */}
      <section className="card" style={{ marginBottom: 'var(--s6)' }}>
        <div className="card__head"><h3>{t('act.monthTitle')}</h3></div>

        <div className="row" style={{ padding: '0 var(--s7)', gap: 'var(--s4)', flexWrap: 'wrap' }}>
          <label>{t('act.period')}
            <select
              className="select"
              value={`${anio}-${mes}`}
              onChange={(e) => {
                const [a, m] = e.target.value.split('-');
                setAnio(Number(a));
                setMes(Number(m));
                setHecho(null);
              }}
            >
              {MESES.map((m) => (
                <option key={m} value={`${anio}-${m}`}>
                  {`${anio}-${String(m).padStart(2, '0')}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        {previo.length === 0 ? (
          <p className="small muted" style={{ padding: 'var(--s6) var(--s7)' }}>
            {activos.length === 0 ? t('act.noneAtAll') : t('act.noneThisMonth')}
          </p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t('act.asset')}</th>
                    <th>{t('act.life')}</th>
                    <th style={{ textAlign: 'right' }}>{t('act.quota')}</th>
                  </tr>
                </thead>
                <tbody>
                  {previo.map((p) => (
                    <tr key={p.activo}>
                      <td>{p.descripcion}</td>
                      <td className="small muted">
                        {t('act.lifeOf', {
                          mes: String(p.mes_de_vida), total: String(p.vida_meses),
                        })}
                        {/* ▶ El último mes lleva lo que falta y no la cuota, para
                            que la acumulada cierre exacta contra el costo. Que se
                            vea distinto evita la pregunta «¿por qué cambió?». */}
                        {p.ultimo && <> · <strong>{t('act.lastMonth')}</strong></>}
                      </td>
                      <td className="amount">{monto({ valor: p.cuota, moneda: p.moneda })}</td>
                    </tr>
                  ))}
                  <tr className="is-total">
                    <td colSpan={2}>{t('act.total')}</td>
                    <td className="amount">
                      {monto({ valor: total.toFixed(2), moneda: previo[0]!.moneda })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {escribe && (
              <div className="row" style={{ padding: 'var(--s7)', gap: 'var(--s5)', flexWrap: 'wrap' }}>
                <button className="btn" type="button" disabled={ocupado} onClick={() => void depreciar()}>
                  {t('act.doIt')}
                </button>
                <span className="small muted">{t('act.twiceIsFine')}</span>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Los bienes ──────────────────────────────────────────────────── */}
      <section className="card">
        <div className="card__head">
          <h3>{t('act.listTitle')}</h3>
          {escribe && (
            <button className="btn btn--ghost btn--sm" type="button"
              onClick={() => setAbrirForm((v) => !v)}>
              {abrirForm ? t('common.cancel') : t('act.add')}
            </button>
          )}
        </div>

        {abrirForm && escribe && (
          <div className="stack" style={{ padding: '0 var(--s7) var(--s7)' }}>
            <p className="small muted">{t('act.addHelp')}</p>
            <div className="grid grid--form">
              <label>{t('act.description')}
                <input className="input" value={nuevo.descripcion}
                  onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })} />
              </label>
              <label>{t('act.accountAsset')} <span className="small muted">· {t('act.onlyAssets')}</span>
                <select className="select" value={nuevo.cuentaActivo}
                  onChange={(e) => setNuevo({ ...nuevo, cuentaActivo: e.target.value })}>
                  <option value="">—</option>
                  {cuentasDeActivo.map((c) => <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>)}
                </select>
              </label>
              <label>{t('act.accountAccum')} <span className="small muted">· {t('act.onlyAssets')}</span>
                <select className="select" value={nuevo.cuentaDepreciacion}
                  onChange={(e) => setNuevo({ ...nuevo, cuentaDepreciacion: e.target.value })}>
                  <option value="">—</option>
                  {cuentasDeActivo.map((c) => <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>)}
                </select>
              </label>
              <label>{t('act.accountExpense')} <span className="small muted">· {t('act.onlyExpenses')}</span>
                <select className="select" value={nuevo.cuentaGasto}
                  onChange={(e) => setNuevo({ ...nuevo, cuentaGasto: e.target.value })}>
                  <option value="">—</option>
                  {cuentasDeGasto.map((c) => <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>)}
                </select>
              </label>
              <label>{t('act.bought')}
                <input className="input" type="date" value={nuevo.fechaCompra}
                  onChange={(e) => setNuevo({ ...nuevo, fechaCompra: e.target.value })} />
              </label>
              {/* ▶ Va aparte de la compra: la norma cuenta desde que el bien está
                  disponible para usarse, y eso no es siempre el día de la factura. */}
              <label>{t('act.start')}
                <input className="input" type="date" value={nuevo.inicio}
                  onChange={(e) => setNuevo({ ...nuevo, inicio: e.target.value })} />
              </label>
              <label>{t('act.cost')}
                <input className="input input--mono" inputMode="decimal" value={nuevo.costo}
                  onChange={(e) => setNuevo({ ...nuevo, costo: e.target.value })} />
              </label>
              <label>{t('act.lifeMonths')}
                <input className="input input--mono" inputMode="numeric" value={nuevo.vidaMeses}
                  onChange={(e) => setNuevo({ ...nuevo, vidaMeses: e.target.value })} />
              </label>
              {/* ▶ VACÍO significa «no difiere», y por eso no tiene valor por
                  omisión como la vida contable. Poner el mismo número en los
                  dos campos es cómo se consigue que un día no coincidan sin que
                  nadie lo quiera. */}
              <label>{t('act.taxLife')}
                <input className="input input--mono" inputMode="numeric" value={nuevo.vidaFiscalMeses}
                  placeholder={t('act.taxLifeSame')}
                  onChange={(e) => setNuevo({ ...nuevo, vidaFiscalMeses: e.target.value })} />
              </label>
              <label>{t('act.residual')}
                <input className="input input--mono" inputMode="decimal" value={nuevo.valorResidual}
                  onChange={(e) => setNuevo({ ...nuevo, valorResidual: e.target.value })} />
              </label>
              {/* ▶ Era un campo de texto, y `department` tiene vocabulario
                  CERRADO desde la 0023: lo que se escribía a mano lo rechazaba
                  la base con `SB100` y no había dónde ver los siete valores.
                  Ver `componentes/CampoDeDimension.tsx`. */}
              <CampoDeDimension
                dimension="department"
                valor={nuevo.department}
                alCambiar={(v) => setNuevo({ ...nuevo, department: v })}
              />
            </div>
            <div className="row" style={{ gap: 'var(--s4)' }}>
              <button
                className="btn" type="button"
                disabled={ocupado || !nuevo.descripcion || !nuevo.cuentaActivo
                  || !nuevo.cuentaDepreciacion || !nuevo.cuentaGasto || !nuevo.costo}
                onClick={() => void correr(async () => {
                  await agregarActivo(pedir, {
                    descripcion: nuevo.descripcion,
                    cuentaActivo: nuevo.cuentaActivo,
                    cuentaDepreciacion: nuevo.cuentaDepreciacion,
                    cuentaGasto: nuevo.cuentaGasto,
                    fechaCompra: nuevo.fechaCompra,
                    inicio: nuevo.inicio,
                    costo: nuevo.costo,
                    vidaMeses: Number(nuevo.vidaMeses),
                    vidaFiscalMeses: nuevo.vidaFiscalMeses.trim() === ''
                      ? null : Number(nuevo.vidaFiscalMeses),
                    valorResidual: nuevo.valorResidual || '0',
                    clasificacion: nuevo.department ? { department: nuevo.department } : {},
                  });
                  setAbrirForm(false);
                  setNuevo({ ...nuevo, descripcion: '', costo: '' });
                })}
              >
                {t('act.save')}
              </button>
            </div>
          </div>
        )}

        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('act.asset')}</th>
                <th>{t('act.start')}</th>
                <th style={{ textAlign: 'right' }}>{t('act.cost')}</th>
                <th>{t('act.lifeMonths')}</th>
                <th>{t('act.accountExpense')}</th>
              </tr>
            </thead>
            <tbody>
              {activos.map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.descripcion}
                    {a.dado_de_baja && (
                      <> <span className="chip chip--soft">{t('act.retired')}</span></>
                    )}
                  </td>
                  <td className="small">{fecha(a.inicio)}</td>
                  <td className="amount">{monto({ valor: a.costo, moneda: a.moneda })}</td>
                  <td className="small mono">
                    {a.vida_meses}
                    {/* ▶ La fiscal sólo se enseña cuando DIFIERE. Enseñar dos
                        números iguales en todas las filas haría que la fila
                        donde no lo son deje de saltar, que es justo la que
                        importa para la declaración. */}
                    {a.vida_fiscal_meses !== null
                      && a.vida_fiscal_meses !== a.vida_meses && (
                      <> <span className="chip chip--warn">
                        {t('act.taxLifeIs', { meses: String(a.vida_fiscal_meses) })}
                      </span></>
                    )}
                  </td>
                  <td className="small mono">{a.cuenta_gasto}</td>
                </tr>
              ))}
              {activos.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: 'var(--s7)' }}>
                    {t('act.noneAtAll')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
